import { useEffect, useSyncExternalStore } from 'react';

// A page the user loses access to while it is open is never replaced on its own (decided 9 October 2026): what
// they typed into a form must never be lost. When access is re-read in place, the page open at that moment is
// "held": every guard that would now redirect or swap it for an access card reports why instead, the page stays,
// and one banner (AccessHoldBanner) says so with a "Leave now". Navigating anywhere releases the hold, and the
// guards behave normally again - so the page that was left shows its access card next time. A hold belongs to the
// org it was taken in: switching org leaves the page just as navigating does, even when the path stays the same.

const listeners = new Set();
let state = { path: null, orgId: null, denial: null };

const emit = () => listeners.forEach((l) => l());
const subscribe = (l) => {
    listeners.add(l);
    return () => listeners.delete(l);
};
const snapshot = () => state;

/** The page at `path`, open in org `orgId`, is held: guards report a refusal there instead of acting on it. */
export const holdPage = (path, orgId = null) => {
    if (!path || (state.path === path && state.orgId === orgId)) return;
    state = { path, orgId, denial: null };
    emit();
};

/** Ends the hold - "Leave now", or the user navigated. Guards act on their refusals again. */
export const releaseHold = () => {
    if (!state.path) return;
    state = { path: null, orgId: null, denial: null };
    emit();
};

/** Ends the hold once the user is no longer on the page it was put on, in the org it was put in. */
export const releaseHoldIfLeft = (pathname, orgId = null) => {
    if (state.path && (state.path !== pathname || state.orgId !== orgId)) releaseHold();
};

/** A guard on the held page would have refused it: remember why, for the banner. The first reason wins. */
export const reportHeldDenial = (path, message) => {
    if (!path || state.path !== path || state.denial) return;
    state = { ...state, denial: { message: message || 'You no longer have access to this page.' } };
    emit();
};

export const getAccessHold = () => state;

/** Whether `pathname` is held right now (a non-hook check for effects and event handlers). */
export const isHeld = (pathname) => !!pathname && state.path === pathname;

/** `{ held, denial }` for the current page, re-rendering when the hold changes. */
export const useAccessHold = (pathname) => {
    const s = useSyncExternalStore(subscribe, snapshot, snapshot);
    const held = !!pathname && s.path === pathname;
    return { held, denial: held ? s.denial : null };
};

/**
 * For a guard: `refused` is what it decided; when the page is held, it reports the refusal (with `message`) and
 * returns true - render the page as it is. Otherwise false - act on the refusal as usual. Call it on every render,
 * before any early return.
 */
export const useHeldRefusal = (pathname, refused, message) => {
    const { held } = useAccessHold(pathname);
    const keep = !!refused && held;
    useEffect(() => {
        if (keep) reportHeldDenial(pathname, message);
    }, [keep, pathname, message]);
    return keep;
};
